import { useCallback, useEffect, useRef, useState } from "react";
import { bandForDeltaE, type LAB } from "../lib/color-engine";
import { pixelToLab, computeDownscaledSize } from "../lib/canvas-utils";
import { matchColor, type RemoteMatch } from "../lib/supabase-client";

const DEBOUNCE_MS = 200;

type ScanState =
  | { status: "idle" }
  | { status: "loading"; tapX: number; tapY: number }
  | { status: "result"; tapX: number; tapY: number; matches: RemoteMatch[] }
  | { status: "error"; message: string };

/**
 * Upload a photo of a physical swatch, tap the color, get the closest
 * catalog matches.
 *
 * Performance choices (see PERFORMANCE_UX_NOTES.md for the fuller list):
 *  - The image is downscaled before being drawn to canvas, so pixel
 *    reads stay fast on a phone even for a large camera photo.
 *  - The tap -> match request is debounced so rapid re-tapping (someone
 *    hunting for the right spot) doesn't fire a request per tap.
 *  - A skeleton result appears immediately on tap, before the network
 *    response returns, so the interaction feels instant regardless of
 *    actual round-trip latency.
 */
export function SwatchScanner() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imageRef = useRef<HTMLImageElement | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const requestIdRef = useRef(0);

  const [imageLoaded, setImageLoaded] = useState(false);
  const [scanState, setScanState] = useState<ScanState>({ status: "idle" });

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const img = new Image();
    img.onload = () => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const { width, height } = computeDownscaledSize(
        img.naturalWidth,
        img.naturalHeight
      );
      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      ctx.drawImage(img, 0, 0, width, height);

      imageRef.current = img;
      setImageLoaded(true);
      setScanState({ status: "idle" });
    };
    img.onerror = () => {
      setScanState({ status: "error", message: "Couldn't load that image — try a different photo." });
    };
    img.src = URL.createObjectURL(file);
  }, []);

  const runMatch = useCallback((lab: LAB, tapX: number, tapY: number) => {
    const thisRequestId = ++requestIdRef.current;

    matchColor(lab, 3)
      .then((matches) => {
        // Ignore stale responses if a newer tap has already fired.
        if (thisRequestId !== requestIdRef.current) return;
        setScanState({ status: "result", tapX, tapY, matches });
      })
      .catch((err: Error) => {
        if (thisRequestId !== requestIdRef.current) return;
        setScanState({ status: "error", message: err.message });
      });
  }, []);

  const handleCanvasClick = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      const canvas = canvasRef.current;
      if (!canvas || !imageLoaded) return;

      const rect = canvas.getBoundingClientRect();
      // Map the click from the canvas's *displayed* CSS size back to its
      // actual pixel buffer size — these differ whenever CSS scales the
      // canvas (e.g. on a narrow phone viewport), so skipping this would
      // sample the wrong pixel on mobile specifically.
      const scaleX = canvas.width / rect.width;
      const scaleY = canvas.height / rect.height;
      const x = (e.clientX - rect.left) * scaleX;
      const y = (e.clientY - rect.top) * scaleY;

      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const lab = pixelToLab(imageData, x, y);

      // Immediate skeleton state — the request hasn't returned yet, but the
      // tap position is shown right away so the interaction feels instant.
      setScanState({ status: "loading", tapX: x / scaleX, tapY: y / scaleY });

      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        runMatch(lab, x / scaleX, y / scaleY);
      }, DEBOUNCE_MS);
    },
    [imageLoaded, runMatch]
  );

  return (
    <div className="flex flex-col gap-4">
      <label
        className={[
          "flex min-h-[44px] w-fit cursor-pointer items-center rounded-md",
          "border border-[#D9D5CC] bg-[#F7F6F3] px-4 text-sm font-medium text-[#232220]",
          "hover:bg-[#EDEBE6]",
        ].join(" ")}
      >
        {imageLoaded ? "Choose a different photo" : "Upload a photo of a swatch"}
        <input
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          onChange={handleFileChange}
        />
      </label>

      <div className="relative w-full max-w-md">
        <canvas
          ref={canvasRef}
          onClick={handleCanvasClick}
          className={[
            "w-full rounded-md border border-[#D9D5CC]",
            imageLoaded ? "cursor-crosshair" : "hidden",
          ].join(" ")}
        />
        {!imageLoaded && (
          <div className="flex aspect-[4/3] w-full items-center justify-center rounded-md border border-dashed border-[#D9D5CC] text-sm text-[#6B6660]">
            Upload a photo, then tap anywhere on it to sample a color
          </div>
        )}

        {(scanState.status === "loading" || scanState.status === "result") && (
          <span
            className="pointer-events-none absolute h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white shadow-[0_0_0_1px_rgba(0,0,0,0.3)]"
            style={{ left: scanState.tapX, top: scanState.tapY }}
          />
        )}
      </div>

      {scanState.status === "loading" && <ResultSkeleton />}

      {scanState.status === "result" && <MatchResults matches={scanState.matches} />}

      {scanState.status === "error" && (
        <p className="text-sm text-red-700">{scanState.message}</p>
      )}
    </div>
  );
}

function ResultSkeleton() {
  return (
    <div className="flex gap-3" aria-live="polite" aria-busy="true">
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          className="h-20 w-24 animate-pulse rounded-md bg-[#E3E0D9]"
        />
      ))}
    </div>
  );
}

function MatchResults({ matches }: { matches: RemoteMatch[] }) {
  if (matches.length === 0 || matches[0].deltaE > 10) {
    return (
      <p className="text-sm text-[#6B6660]">
        No close match found in the catalog for that spot — try tapping a
        more evenly lit part of the swatch.
      </p>
    );
  }

  return (
    <div className="flex gap-3" aria-live="polite">
      {matches.map((match) => {
        const { label } = bandForDeltaE(match.deltaE);
        return (
          <div
            key={match.id}
            className="flex w-24 flex-col overflow-hidden rounded-md border border-[#D9D5CC]"
          >
            <div className="h-14 w-full" style={{ backgroundColor: match.hex }} />
            <div className="flex flex-col gap-0.5 px-2 py-1.5">
              <span className="truncate text-xs font-medium text-[#232220]">
                {match.name}
              </span>
              <span className="font-mono text-[10px] text-[#6B6660]">
                {match.shadeCode}
              </span>
              <span className="text-[10px] text-[#6B6660]">{label}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}
